<template>
  <div class="row">
    <div class="col-12">
      <h5 data-cy="application-form-section-documents">
        {{ $t('message.applicationForm.documents') }}
      </h5>
    </div>
    <div
      v-if="object && !object.acceptsCreditTrust && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM'"
      class="col-12 col-md-6 mb-2">
      <div>
        <div class="d-inline-block">
          <b>{{ $t('message.applicationForm.debtEnforcementRegister') }}</b><br>
          <span class="maxFileSize">max. 50MB</span>
        </div>
        <coozzy-button
          class="float-right"
          data-cy="application-form-individual-upload-debt-register-button"
          design="green"
          @click="uploadDocumentClicked('debtUpload')">
          {{ $t('message.manageDocuments.addDocument') }}
        </coozzy-button>
      </div>
      <coozzy-spinner
        v-if="debtUploadProcessing"
        class="align-self-center" />
      <div
        v-for="item in debtEnforcementRegisterMedia"
        :key="item.id"
        class="media-block col">
        <div
          class="file-block p-1 my-2">
          <a
            :href="item.url"
            data-v-09485260=""
            target="_blank">
            {{ item.filename }}
          </a>
          <coozzy-button
            class="delete-icon"
            design="transparent"
            @click="deleteMedia(item.id, 'debtEnforcementRegisterMediaIds')">
            <coozzy-delete-icon />
          </coozzy-button>
        </div>
      </div>
      <coozzy-form-file-input
        ref="debtUpload"
        v-model="debtUpload"
        accept="text/plain, text/csv, application/pdf, image/*, video/*, .doc, .docx, .xls, .xlsx, .ppt, .pptx"
        class="d-none"
        @change="addFile($event, 'debtEnforcementRegisterMediaIds')" />
    </div>
    <div class="col-12 col-md-6 mb-2">
      <div>
        <div class="d-inline-block">
          <b>{{ $t('message.applicationForm.idCard') }}</b><br>
          <span class="maxFileSize">max. 50MB</span>
        </div>
        <coozzy-button
          class="float-right"
          data-cy="application-form-individual-upload-id-card-button"
          design="green"
          @click="uploadDocumentClicked('idCardUpload')">
          {{ $t('message.manageDocuments.addDocument') }}
        </coozzy-button>
      </div>
      <coozzy-spinner
        v-if="idCardUploadProcessing"
        class="align-self-center" />
      <div
        v-for="item in idCardMedia"
        :key="item.id"
        class="media-block col">
        <div
          class="file-block p-1 my-2">
          <a
            :href="item.url"
            target="_blank">
            {{ item.filename }}
          </a>
          <coozzy-button
            class="delete-icon"
            design="transparent"
            @click="deleteMedia(item.id, 'idCardMediaIds')">
            <coozzy-delete-icon />
          </coozzy-button>
        </div>
      </div>
      <coozzy-form-file-input
        ref="idCardUpload"
        v-model="idCardUpload"
        accept="text/plain, text/csv, application/pdf, image/*, video/*, .doc, .docx, .xls, .xlsx, .ppt, .pptx"
        class="d-none"
        @change="addFile($event, 'idCardMediaIds')" />
    </div>
    <div class="col-12 col-md-6">
      <div>
        <div class="d-inline-block">
          <b>{{ $t('message.applicationForm.media') }}</b><br>
          <span class="maxFileSize">max. 50MB</span>
        </div>
        <coozzy-button
          class="float-right"
          data-cy="application-form-individual-upload-additional-documents-button"
          design="green"
          @click="uploadDocumentClicked('mediaUpload')">
          {{ $t('message.manageDocuments.addDocument') }}
        </coozzy-button>
      </div>
      <coozzy-spinner
        v-if="mediaUploadProcessing"
        class="align-self-center" />
      <div
        v-for="item in media"
        :key="item.id"
        class="media-block col">
        <div
          class="file-block p-1 my-2">
          <a
            :href="item.url"
            target="_blank">
            {{ item.filename }}
          </a>
          <coozzy-button
            class="delete-icon"
            design="transparent"
            @click="deleteMedia(item.id, 'mediaIds')">
            <coozzy-delete-icon />
          </coozzy-button>
        </div>
      </div>
      <coozzy-form-file-input
        ref="mediaUpload"
        v-model="mediaUpload"
        accept="text/plain, text/csv, application/pdf, image/*, video/*, .doc, .docx, .xls, .xlsx, .ppt, .pptx"
        class="d-none"
        @change="addFile($event, 'mediaIds')" />
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { mapGetters, mapMutations } from 'vuex'
import MediaApi from '@/misc/apis/MediaApi'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon.vue'
import CoozzyFormFileInput from '@/framework/components/form/fileInput/CoozzyFormFileInput.vue'
import { media } from '@/mixins/media'
import {
  ApplicationFormDocumentMediaRow,
  ApplicationFormIndividual,
  ApplicationFormObjectDocumentsContext
} from '@/types/models'

type IndividualMediaIdField =
  | 'debtEnforcementRegisterMediaIds'
  | 'idCardMediaIds'
  | 'mediaIds'

interface IndividualDocumentsData {
  debtEnforcementRegisterMedia: ApplicationFormDocumentMediaRow[]
  idCardMedia: ApplicationFormDocumentMediaRow[]
  media: ApplicationFormDocumentMediaRow[]
  mediaUpload: File | null
  mediaUploadProcessing: boolean
  idCardUpload: File | null
  idCardUploadProcessing: boolean
  debtUpload: File | null
  debtUploadProcessing: boolean
}

export default Vue.extend({
  name: 'ApplicationFormIndividualDocuments',
  components: {
    CoozzyFormFileInput,
    CoozzyDeleteIcon,
    CoozzyButton,
    CoozzySpinner
  },
  mixins: [media],
  props: {
    individualId: {
      type: String,
      required: true
    },
    object: {
      type: Object as PropType<ApplicationFormObjectDocumentsContext | null>,
      default: null
    }
  },
  data(): IndividualDocumentsData {
    return {
      debtEnforcementRegisterMedia: [],
      idCardMedia: [],
      media: [],
      mediaUpload: null,
      mediaUploadProcessing: false,
      idCardUpload: null,
      idCardUploadProcessing: false,
      debtUpload: null,
      debtUploadProcessing: false
    }
  },
  computed: {
    ...mapGetters('applicationForm', ['getIndividualByInternalId']),
    individual(): ApplicationFormIndividual {
      return this.getIndividualByInternalId(this.individualId) as ApplicationFormIndividual
    },
    debtEnforcementRegisterMediaIds: {
      get(): string[] {
        return this.individual.debtEnforcementRegisterMediaIds
          ? this.individual.debtEnforcementRegisterMediaIds
          : []
      },
      set(value: string[]) {
        const ind = this.individual
        ind.debtEnforcementRegisterMediaIds = value
        this.updateIndividual(ind)
      }
    },
    idCardMediaIds: {
      get(): string[] {
        return this.individual.idCardMediaIds ? this.individual.idCardMediaIds : []
      },
      set(value: string[]) {
        const ind = this.individual
        ind.idCardMediaIds = value
        this.updateIndividual(ind)
      }
    },
    mediaIds: {
      get(): string[] {
        return this.individual.mediaIds ? this.individual.mediaIds : []
      },
      set(value: string[]) {
        const ind = this.individual
        ind.mediaIds = value
        this.updateIndividual(ind)
      }
    }
  },
  // watch: {
  //   mediaUpload: function (newValue) {
  //     if (newValue !== null) {
  //       this.mediaUploadProcessing = true
  //       this.documentCreated(newValue, 'mediaIds')
  //     }
  //   },
  //   idCardUpload: function (newValue) {
  //     if (newValue !== null) {
  //       this.idCardUploadProcessing = true
  //       this.documentCreated(newValue, 'idCardMediaIds')
  //     }
  //   },
  //   debtUpload: function (newValue) {
  //     if (newValue !== null) {
  //       this.debtUploadProcessing = true
  //       this.documentCreated(newValue, 'debtEnforcementRegisterMediaIds')
  //     }
  //   }
  // },
  mounted() {
    const promises: Promise<ApplicationFormDocumentMediaRow>[] = []
    if (this.debtEnforcementRegisterMediaIds.length > 0) {
      this.debtEnforcementRegisterMediaIds.forEach(x =>
        promises.push(MediaApi.getMediaByID(x) as Promise<ApplicationFormDocumentMediaRow>)
      )
    }
    if (this.idCardMediaIds.length > 0) {
      this.idCardMediaIds.forEach(x =>
        promises.push(MediaApi.getMediaByID(x) as Promise<ApplicationFormDocumentMediaRow>)
      )
    }
    if (this.mediaIds.length > 0) {
      this.mediaIds.forEach(x =>
        promises.push(MediaApi.getMediaByID(x) as Promise<ApplicationFormDocumentMediaRow>)
      )
    }
    Promise.all(promises)
      .then(response => {
        for (let index = 0; index < response.length; index++) {
          const row = response[index]
          if (this.debtEnforcementRegisterMediaIds.find(x => x === row.id)) {
            this.debtEnforcementRegisterMedia.push(row)
          }
          if (this.idCardMediaIds.find(x => x === row.id)) {
            this.idCardMedia.push(row)
          }
          if (this.mediaIds.find(x => x === row.id)) {
            this.media.push(row)
          }
        }
      })
      .catch(e => {
        console.log(e)
      })
  },
  methods: {
    ...mapMutations('applicationForm', ['updateIndividual']),
    uploadDocumentClicked(ref: string) {
      if (!this.readOnly) {
        const comp = this.$refs[ref] as Vue & { $el: HTMLElement }
        const btn = comp.$el.children
        ;(btn[0] as HTMLElement).click()
      }
    },
    async addFile(event: Event, source: IndividualMediaIdField) {
      if (source === 'debtEnforcementRegisterMediaIds') {
        this.debtUploadProcessing = true
      } else if (source === 'idCardMediaIds') {
        this.idCardUploadProcessing = true
      } else if (source === 'mediaIds') {
        this.mediaUploadProcessing = true
      }
      const files = await this.getFilesFromEvent(event)
      if (files && files.length > 0) {
        const file = files[0] as File
        await this.documentCreated(file, source)
      }

      if (source === 'debtEnforcementRegisterMediaIds') {
        this.debtUploadProcessing = false
      } else if (source === 'idCardMediaIds') {
        this.idCardUploadProcessing = false
      } else if (source === 'mediaIds') {
        this.mediaUploadProcessing = false
      }
    },
    async documentCreated(document: File, suffix: IndividualMediaIdField) {
      const docSizeMB = document.size / (1024 * 1024)
      if (docSizeMB > 50) {
        this.mediaUploadProcessing = false
        this.debtUploadProcessing = false
        this.idCardUploadProcessing = false
        Vue.toasted.show(this.$tc('message.uploadingErrors.maxFileSize'), { type: 'error' })
      } else {
        const selectedFile = [document]
        const response = await this.uploadMedia(selectedFile)
        if (response && response.length > 0) {
          const uploaded = response[0]
          const mediaRow: ApplicationFormDocumentMediaRow = {
            id: uploaded.id,
            url: uploaded.url,
            filename: uploaded.filename
          }
          if (suffix === 'debtEnforcementRegisterMediaIds') {
            this.debtEnforcementRegisterMediaIds = [
              ...this.debtEnforcementRegisterMediaIds,
              uploaded.id
            ]
            this.debtEnforcementRegisterMedia.push(mediaRow)
          } else if (suffix === 'idCardMediaIds') {
            this.idCardMediaIds = [...this.idCardMediaIds, uploaded.id]
            this.idCardMedia.push(mediaRow)
          } else {
            this.mediaIds = [...this.mediaIds, uploaded.id]
            this.media.push(mediaRow)
          }
          this.mediaUpload = null
          this.debtUpload = null
          this.idCardUpload = null
        }
        this.mediaUploadProcessing = false
        this.debtUploadProcessing = false
        this.idCardUploadProcessing = false
      }
    },
    deleteMedia(id: string, suffix: IndividualMediaIdField) {
      if (suffix === 'debtEnforcementRegisterMediaIds') {
        this.debtEnforcementRegisterMediaIds = this.debtEnforcementRegisterMediaIds.filter(
          e => e !== id
        )
        this.debtEnforcementRegisterMedia = this.debtEnforcementRegisterMedia.filter(
          el => el.id !== id
        )
      } else if (suffix === 'idCardMediaIds') {
        this.idCardMediaIds = this.idCardMediaIds.filter(e => e !== id)
        this.idCardMedia = this.idCardMedia.filter(el => el.id !== id)
      } else {
        this.mediaIds = this.mediaIds.filter(e => e !== id)
        this.media = this.media.filter(el => el.id !== id)
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.maxFileSize {
  font-size: 0.875rem;
}
</style>
